import { Fragment, forwardRef, useContext, useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import classes from "./Modal.module.css";
import { ModalElementContext } from "src/context/modal-element-context";

import { nanoid } from "@reduxjs/toolkit";
import { motion } from "framer-motion";
import { MODAL_ANIMATION_TIME } from "src/constants";

function Backdrop({ isClosing }) {
  const animClass = isClosing ? classes.fadeout : classes.fadein;
  return <div className={`${classes.backdrop} ${animClass}`} />;
}

const modalVariants = {
  hidden: {
    opacity: 0,
    scale: 0.85,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      type: "tween",
      ease: "easeIn",
      duration: 0.1,
    },
  },
  closed: {
    opacity: 0,
    scale: 0.85,
    transition: {
      type: "tween",
      ease: "easeOut",
      duration: 0.15,
    },
  },
};

const FullScreenOverlay = forwardRef(function FullScreenOverlay({ children, isClosing }, ref) {
  return (
    <motion.dialog
      initial={"hidden"}
      animate={isClosing ? "closed" : "visible"}
      variants={modalVariants}
      ref={ref}
      className={`${classes.modal} ${classes.fullscreen}`}>
      {children}
    </motion.dialog>
  );
});

const RegularOverlay = forwardRef(function RegularOverlay(
  { children, isClosing, onBackdropClick, clickSelectors = [] },
  ref
) {
  const elId = useId();
  const uid = nanoid();

  useEffect(() => {
    const dialog = document.getElementById(elId);

    const onDetectOutsideClick = (event) => {
      console.log("modal");
      console.log(event.target);
      const rect = dialog.getBoundingClientRect();
      const isDialogClick =
        rect.top <= event.clientY &&
        event.clientY <= rect.top + rect.height &&
        rect.left <= event.clientX &&
        event.clientX <= rect.left + rect.width;
      const isInnerContentClick = event.target.closest("#modal-content" + uid);
      const selectedItemClicked = clickSelectors.some((csel) => event.target.closest(csel));
      console.log(isInnerContentClick);
      if (!isDialogClick && !isInnerContentClick && !selectedItemClicked) {
        onBackdropClick();
      }
    };
    // dialog.showModal();
    dialog.addEventListener("click", onDetectOutsideClick);
    return () => {
      document.removeEventListener("click", onDetectOutsideClick);
    };
  }, []);

  return (
    <motion.dialog
      initial={"hidden"}
      animate={isClosing ? "closed" : "visible"}
      variants={modalVariants}
      id={elId}
      ref={ref}
      className={`${classes.modal} ${classes.halfscreen}`}>
      <div id={"modal-content" + uid}>{children}</div>
    </motion.dialog>
  );
});

const portalElement = document.getElementById("overlays");

function Modal({
  isClosing,
  fullscreen = false,
  children,
  clickSelectors = [],
  disablePointerEvents,
}) {
  const modalRef = useRef();
  const { onClose } = useContext(ModalElementContext);

  useEffect(() => {
    document.body.classList.add(classes.body);
    document.getElementsByTagName("html")[0].style.scrollbarWidth = "none";
    let closingTimer;
    if (modalRef.current?.open && isClosing) {
      closingTimer = setTimeout(() => modalRef.current?.close(), MODAL_ANIMATION_TIME);
    } else if (!modalRef.current?.open && !isClosing) {
      modalRef.current?.showModal();
    }
    return () => {
      document.body.classList.remove(classes.body);
      document.getElementsByTagName("html")[0].style.scrollbarWidth = "unset";
      clearTimeout(closingTimer);
    };
  }, [isClosing]);

  const overlay = fullscreen ? (
    <FullScreenOverlay ref={modalRef} isClosing={isClosing}>
      {children}
    </FullScreenOverlay>
  ) : (
    <RegularOverlay
      clickSelectors={clickSelectors}
      ref={modalRef}
      onBackdropClick={disablePointerEvents ? () => {} : onClose}
      isClosing={isClosing}>
      {children}
    </RegularOverlay>
  );

  return (
    <Fragment>
      {createPortal(<Backdrop onClick={onClose} isClosing={isClosing} />, portalElement)}
      {createPortal(overlay, portalElement)}
    </Fragment>
  );
}

export default Modal;
